import {AutomationNode, NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import type {FlowPreview, PreviewMember} from 'crqlar-react-common/dist/models/automation/flowPreview.model';

// Members of the first Audience / Team members step the run reaches (`FANNED_OUT` in the preview). The API
// filters exactly that step (`memberNodeId`); a fan-out inside a member's path is neither offered nor filtered.
export interface TestMemberChoice {
    nodeId: string;
    /** `PROFILE` for guests, `USER` for team members. */
    memberType: string;
    /** Each person once, in the preview's order. Every one has an id. */
    members: PreviewMember[];
    /** Everyone the step reaches right now, when the preview knows; the list can hold fewer. */
    total?: number | null;
}

/** Undefined when the run reaches no Audience / Team members step right now, or nothing was previewed. */
export const testMemberChoice = (preview?: FlowPreview): TestMemberChoice | undefined => {
    const nodeId = preview?.once?.find(step => step.outcome === 'FANNED_OUT')?.nodeId;
    if (!preview || !nodeId) return undefined;

    const fanOut = preview.fanOuts?.find(entry => entry.nodeId === nodeId);
    const seen = new Set<string>();
    const members = (preview.members ?? []).filter(member => {
        if (member.fanOutNodeId !== nodeId || !member.id || seen.has(member.id)) return false;
        seen.add(member.id);
        return true;
    });

    return {nodeId, memberType: fanOut?.memberType ?? 'PROFILE', members, total: fanOut?.total};
};

// Prefers a member whose path sends an email, so the first test puts something in the inbox.
export const defaultTestMemberId = (members: PreviewMember[], nodes: AutomationNode[]): string | undefined => {
    const emailSteps = new Set(nodes.filter(node => node.type === NodeType.SEND_EMAIL).map(node => node.id));
    const sendsEmail = (member: PreviewMember) => (member.steps ?? []).some(step => (
        step.outcome === 'SENT' && emailSteps.has(step.nodeId)
    ));

    return (members.find(sendsEmail) ?? members.find(member => !!member.email) ?? members[0])?.id;
};

export const matchesTestMember = (member: PreviewMember, query: string): boolean => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;

    return [member.name, member.email].some(value => !!value && value.toLowerCase().includes(needle));
};
